import { useLazyQuery, useMutation, useQuery } from '@apollo/client'
import moment from 'moment'
import React from 'react'
import { Badge, Modal, Spinner, Button, Form, Table, OverlayTrigger, Tooltip } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useIntl } from 'react-intl'
import {
  ASSIGN_COMMUNICATION_LOG,
  GET_CHANNEL_COMMUNICATION_LOGS,
  SYNC_TWILIO_MESSAGES_NOW,
} from '../../../gql/queries/channelConfigAdminQuery'
import { GET_ASSIGNMENT_USERS_LIST } from '../../../gql/queries/usersQuery'
import { DateInput } from '../../../components/DateInput'
import {
  CHANNEL_CONFIG_MESSAGES as M,
  DIRECTION_VALUES,
  EM_DASH,
  directionLabel,
} from '../channelConfigMessages'
import {TELEPHONY_MESSAGES as TM} from '../../../helpers/telephonyMessages'

// Nombre y apellido son datos; se unen aquí para no dejar el separador suelto.
const contactName = (first?: string, last?: string) =>
  `${first || ''} ${last || ''}`.trim() || EM_DASH

interface IChannelMessagesModalProps {
  isOpen: boolean
  onClose: () => void
  phoneNumber?: string
  channelKind?: 'SMS' | 'WHATSAPP' | 'EMAIL'
  ownerLabel?: string
}

// Estados servidos por el backend: se pintan en crudo, no se traducen.
const STATUS_OPTIONS = ['SENT', 'DELIVERED', 'READ', 'FAILED', 'RECEIVED']
const getStatusBadge = (status: string) => {
  const s = (status || '').toUpperCase()
  if (s === 'SENT') return 'primary'
  if (s === 'DELIVERED') return 'info'
  if (s === 'READ') return 'success'
  if (s === 'FAILED') return 'danger'
  if (s === 'RECEIVED') return 'warning'
  return 'secondary'
}

const isTwilioSyncable = (channelKind?: string) => channelKind === 'SMS' || channelKind === 'WHATSAPP'

export const ChannelMessagesModal: React.FC<IChannelMessagesModalProps> = ({
  isOpen,
  onClose,
  phoneNumber,
  channelKind,
  ownerLabel,
}) => {
  const intl = useIntl()
  const [searchTerm, setSearchTerm] = React.useState('')
  const [debouncedSearch, setDebouncedSearch] = React.useState('')
  const [statusFilter, setStatusFilter] = React.useState('')
  const [directionFilter, setDirectionFilter] = React.useState('')
  const [createdAfter, setCreatedAfter] = React.useState<string | null>(null)
  const [createdBefore, setCreatedBefore] = React.useState<string | null>(null)
  const [lastSync, setLastSync] = React.useState<{ created?: number; updated?: number } | null>(null)
  const [allEdges, setAllEdges] = React.useState<any[]>([])

  const [fetchLogs, { data, loading, error, fetchMore }] = useLazyQuery(GET_CHANNEL_COMMUNICATION_LOGS, {
    fetchPolicy: 'network-only',
  })
  const [syncTwilioNow, { loading: syncing }] = useMutation(SYNC_TWILIO_MESSAGES_NOW)
  const [assignCommunicationLog, { loading: assigning }] = useMutation(ASSIGN_COMMUNICATION_LOG)
  const { data: usersData } = useQuery(GET_ASSIGNMENT_USERS_LIST, {
    variables: { assignmentCandidates: true },
    skip: !isOpen,
  })
  const assignableUsers = usersData?.allUsersList || []

  React.useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const baseVariables = React.useMemo(
    () => ({
      channelPhoneNumber: phoneNumber,
      channel: channelKind,
      content: debouncedSearch || undefined,
      status: statusFilter || undefined,
      direction: directionFilter || undefined,
      createdAfter: createdAfter || undefined,
      createdBefore: createdBefore || undefined,
    }),
    [phoneNumber, channelKind, debouncedSearch, statusFilter, directionFilter, createdAfter, createdBefore]
  )

  const runSearch = React.useCallback(async () => {
    if (!phoneNumber) return
    if (isTwilioSyncable(channelKind)) {
      try {
        const result = await syncTwilioNow({ variables: { channelPhoneNumber: phoneNumber, channelKind } })
        const payload = result.data?.syncTwilioMessagesNow
        if (payload?.error) {
          toast.warning(intl.formatMessage(M.toastSyncFailedWithError, {message: payload.error}))
        } else if (payload?.success) {
          setLastSync({ created: payload.created, updated: payload.updated })
        }
      } catch (e: any) {
        toast.warning(intl.formatMessage(M.toastSyncFailedLocal))
      }
    }
    const res = await fetchLogs({ variables: baseVariables })
    setAllEdges(res.data?.allCommunications?.edges || [])
  }, [intl, phoneNumber, channelKind, baseVariables, fetchLogs, syncTwilioNow])

  React.useEffect(() => {
    if (isOpen && phoneNumber) {
      runSearch()
    }
    if (!isOpen) {
      setAllEdges([])
      setLastSync(null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, phoneNumber])

  const handleLoadMore = async () => {
    const endCursor = data?.allCommunications?.pageInfo?.endCursor
    if (!endCursor) return
    const res = await fetchMore({ variables: { ...baseVariables, after: endCursor } })
    setAllEdges((prev) => [...prev, ...(res.data?.allCommunications?.edges || [])])
  }

  const hasNextPage = data?.allCommunications?.pageInfo?.hasNextPage

  // `node.user.id` may come back as a raw pk or as a base64 relay global id
  // (`TypeName:pk`) depending on which GraphQL type resolved the FK, while
  // `allUsersList` always returns raw pks — normalize both to compare.
  const toRawUserId = (id: any): string => {
    if (id === null || id === undefined) return ''
    const str = String(id)
    try {
      const decoded = atob(str)
      if (decoded.includes(':')) return decoded.split(':').pop() || str
    } catch {
      // not base64, fall through
    }
    return str
  }

  const handleAssign = async (logId: string, userId: string) => {
    try {
      const result = await assignCommunicationLog({
        variables: { id: logId, userId: userId ? Number(userId) : null },
      })
      const payload = result.data?.assignCommunicationLog
      if (!payload?.success) {
        toast.error(payload?.errors?.[0]?.message || intl.formatMessage(M.toastAssignFailed))
        return
      }
      setAllEdges((prev) =>
        prev.map((edge) =>
          edge.node.id === logId ? { ...edge, node: { ...edge.node, user: payload.communicationLog.user } } : edge
        )
      )
    } catch (e: any) {
      toast.error(e?.message || intl.formatMessage(M.toastAssignFailed))
    }
  }

  return (
    <Modal className='modal-dialog-communication-logs' show={isOpen} onHide={onClose} size='xl' scrollable centered>
      <Modal.Header closeButton>
        <Modal.Title className='m-0'>
          {intl.formatMessage(M.messagesTitle, { channel: channelKind })}
          {phoneNumber && <span className='text-muted fs-7 ms-2'>{phoneNumber}</span>}
          {ownerLabel && <div className='text-muted fs-8'>{ownerLabel}</div>}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ minHeight: '400px' }}>
        <div className='d-flex flex-wrap align-items-end gap-2 mb-3'>
          <Form.Group style={{ minWidth: '220px', flex: '1 1 220px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>
              {intl.formatMessage(M.messagesSearchLabel)}
            </Form.Label>
            <Form.Control
              type='text'
              size='sm'
              placeholder={intl.formatMessage(M.messagesSearchPlaceholder)}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>
              {intl.formatMessage(M.status)}
            </Form.Label>
            <Form.Select size='sm' value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value=''>{intl.formatMessage(M.messagesAllStatuses)}</option>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <Form.Group style={{ minWidth: '140px' }}>
            <Form.Label className='fs-8 fw-semibold text-gray-700 mb-1'>
              {intl.formatMessage(TM.direction)}
            </Form.Label>
            <Form.Select size='sm' value={directionFilter} onChange={(e) => setDirectionFilter(e.target.value)}>
              <option value=''>{intl.formatMessage(M.messagesAllDirections)}</option>
              {DIRECTION_VALUES.map((value) => (
                <option key={value} value={value}>
                  {directionLabel(intl, value)}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          <div style={{ minWidth: '140px' }}>
            <DateInput
              id='channel-messages-created-after'
              name='createdAfter'
              label={
                <span className='fs-8 fw-semibold text-gray-700'>
                  {intl.formatMessage(M.messagesDateFrom)}
                </span>
              }
              value={createdAfter}
              onChange={(_field, value) => setCreatedAfter(value || null)}
            />
          </div>
          <div style={{ minWidth: '140px' }}>
            <DateInput
              id='channel-messages-created-before'
              name='createdBefore'
              label={
                <span className='fs-8 fw-semibold text-gray-700'>
                  {intl.formatMessage(M.messagesDateTo)}
                </span>
              }
              value={createdBefore}
              onChange={(_field, value) => setCreatedBefore(value || null)}
            />
          </div>
          <Button variant='primary' size='sm' onClick={runSearch} disabled={syncing}>
            {syncing ? <Spinner animation='border' size='sm' className='me-1' /> : <i className='bi bi-search me-1' />}
            {intl.formatMessage(M.search)}
          </Button>
        </div>

        {isTwilioSyncable(channelKind) && (
          <div className='text-muted fs-8 mb-3'>
            <i className='bi bi-arrow-repeat me-1' />
            {syncing
              ? intl.formatMessage(M.messagesSyncing)
              : lastSync
              ? intl.formatMessage(M.messagesSyncedJustNow, {
                  created: String(lastSync.created || 0),
                  updated: String(lastSync.updated || 0),
                })
              : intl.formatMessage(M.messagesSyncsOnSearch)}
          </div>
        )}

        {loading && allEdges.length === 0 ? (
          <div className='d-flex flex-column align-items-center justify-content-center p-5'>
            <Spinner animation='border' variant='primary' />
            <span className='mt-2 text-muted'>{intl.formatMessage(M.messagesLoading)}</span>
          </div>
        ) : error ? (
          <div className='alert alert-danger'>
            <i className='bi bi-exclamation-triangle-fill me-2'></i>
            {intl.formatMessage(M.messagesLoadError, { message: error.message })}
          </div>
        ) : allEdges.length === 0 ? (
          <div className='text-center p-5'>
            <i className='bi bi-chat-left-text text-muted fs-1 mb-3 d-block'></i>
            <p className='text-muted'>{intl.formatMessage(M.messagesEmpty)}</p>
          </div>
        ) : (
          <div className='table-responsive'>
            <Table hover size='sm' className='align-middle'>
              <thead>
                <tr className='text-muted fw-bold fs-8 text-uppercase'>
                  <th>{intl.formatMessage(M.messagesColDate)}</th>
                  <th>{intl.formatMessage(TM.direction)}</th>
                  <th>{intl.formatMessage(M.status)}</th>
                  <th>{intl.formatMessage(M.contact)}</th>
                  <th>{intl.formatMessage(M.message)}</th>
                  <th>{intl.formatMessage(M.messagesColAssignedTo)}</th>
                </tr>
              </thead>
              <tbody>
                {allEdges.map(({ node }: any) => {
                  const isOutbound = node.direction === 'OUTBOUND'
                  const contact = node.contact
                  return (
                    <tr key={node.id}>
                      <td className='text-nowrap fs-8'>{moment(node.created).format('MMM DD, YYYY hh:mm A')}</td>
                      <td>
                        <Badge bg={isOutbound ? 'light-primary' : 'light-success'} text='dark' className='fs-9'>
                          {intl.formatMessage(isOutbound ? M.messagesSent : M.messagesReceived)}
                        </Badge>
                      </td>
                      <td>
                        <Badge bg={getStatusBadge(node.status)} className='fs-9'>
                          {node.status}
                        </Badge>
                      </td>
                      <td className='fs-8'>
                        {contact ? contactName(contact.firstName, contact.lastName) : EM_DASH}
                      </td>
                      <td className='fs-8 text-break' style={{ maxWidth: '400px' }}>
                        <OverlayTrigger
                          overlay={
                            <Tooltip>
                              {node.content || intl.formatMessage(M.messagesNoContent)}
                            </Tooltip>
                          }
                        >
                          <span className='text-truncate d-inline-block' style={{ maxWidth: '400px' }}>
                            {node.content || (
                              <span className='fst-italic text-muted'>
                                {intl.formatMessage(M.messagesNoContent)}
                              </span>
                            )}
                          </span>
                        </OverlayTrigger>
                      </td>
                      <td className='fs-8' style={{ minWidth: '160px' }}>
                        <Form.Select
                          size='sm'
                          value={toRawUserId(node.user?.id)}
                          disabled={assigning}
                          onChange={(e) => handleAssign(node.id, e.target.value)}
                        >
                          <option value=''>{intl.formatMessage(M.messagesUnassigned)}</option>
                          {assignableUsers.map((u: any) => (
                            <option key={u.id} value={u.id}>
                              {`${u.firstName || ''} ${u.lastName || ''}`.trim()}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </Table>
            {hasNextPage && (
              <div className='text-center mt-2'>
                <Button variant='light-primary' size='sm' onClick={handleLoadMore} disabled={loading}>
                  {loading ? <Spinner animation='border' size='sm' className='me-1' /> : null}
                  {intl.formatMessage(M.messagesLoadMore)}
                </Button>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} size='sm'>
          {intl.formatMessage(M.close)}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}
